import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  IconButton,
  Icon,
  useToast,
  Spinner,
  Tooltip,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import CSSAvatar from "./CSSAvatar.jsx";
import {IoArrowBack, IoSettingsOutline} from "react-icons/io5";
import {FiFile, FiImage, FiHash, FiUsers} from "react-icons/fi";
import {BsDot, BsThreeDots} from "react-icons/bs";
import {MdDelete} from "react-icons/md";
import {format, isToday, isYesterday, isSameDay} from "date-fns";
import {Node} from "slate";
import {nanoid} from "nanoid";
import {chatReducer, initialChatState} from "../additional_files/chatReducer";
import {useChatSocket} from "../additional_files/custom";
import {url} from "../crudRequests";
import {crudStorageUpload, getFileUrl} from "../storageRequests";
import {fileProxy} from "./helpers/fileProxy";
import {MessagingContext, GroupManagementContext} from "./PatientChat";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import FormattedText from "./FormattedText";
import MessagingFilePreview from "./MessagingFilePreview";
import {patientMessagingApi} from "../features/messaging/api";
import {
  buildPatientSend,
  deletePatientMessage,
  ensureServerConversation,
} from "../features/messaging/patientSend";
import {entriesOf} from "../features/messaging/outbox";
import {PendingMessages} from "../features/messaging/PendingMessages";

// No outbox (a screen outside the chat page): nothing waits.
const NO_ENTRIES = [];
const noSubscribe = () => () => {};
const noEntries = () => NO_ENTRIES;

/** A draft 1:1's key, until its first send creates the conversation (outbox.js `prepare`). */
const draftKeyOf = (conversation) =>
  conversation?.draftTarget ? `draft:${conversation.draftTarget.kind}:${conversation.draftTarget.id}` : null;

// A clinic that does not let the patient start a conversation: the send's refusal, never retried.
function initiateBlockedError() {
  return Object.assign(new Error("This clinic doesn't let you start a conversation here."), {status: 403, code: "PATIENT_INITIATE_DISABLED"});
}

export default function StaffChatArea({
  user,
  onBack,
  loadConversations,
  onConversationCreated,
  isPatientView = false,
  currentUserId: propCurrentUserId,
  schState: propSchState,
  openGroupManagement: propOpenGroupManagement,
  canSendMessage = true,
}) {
  // Try to get from context first, fall back to props
  const contextValue = useContext(MessagingContext);
  const groupContextValue = useContext(GroupManagementContext);

  const schState = contextValue?.schState || propSchState;
  const openGroupManagement =
    groupContextValue?.openGroupManagement || propOpenGroupManagement;
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const toast = useToast();
  const auth = useMemo(() => ({jwt: schState?.jwt}), [schState?.jwt]);
  // The patient's messages on their way (outbox.js, held by PatientChat; ADR 0018 §5).
  const outbox = contextValue?.outbox ?? null;
  const storedListeners = contextValue?.storedListeners ?? null;

  // Refs
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // State
  const [inputValue, setInputValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [initiateBlocked, setInitiateBlocked] = useState(false);

  // The open conversation: the parent's row, or the server row this thread
  // created from a draft on its first send. Every id below is read from here.
  const [activeConversation, setActiveConversation] = useState(user);
  const loadedIdRef = useRef(undefined);
  useEffect(() => {
    setActiveConversation(user);
  }, [user.conversationId, user.draftTarget?.kind, user.draftTarget?.id]);

  // Get conversation data from state
  const {conversations, selectedChat, loading, messages = []} = state;

  // Get current user ID - support both patient and staff contexts
  const currentUserId =
    propCurrentUserId ||
    schState.doctor?.did ||
    schState.admin?.id ||
    schState.patient?.pid;

  // A draft has no id until the server creates the conversation.
  const conversationId = activeConversation?.conversationId || null;

  // Memoize rooms array to prevent unnecessary reconnections
  const rooms = React.useMemo(() => [conversationId], [conversationId]);

  // Initialize socket connection - passing patient object or creating a minimal one
  const patientObj = schState.patient || {pid: currentUserId};
  const socket = useChatSocket(url + "/chat", rooms, patientObj, dispatch, {
    // The patient's own message, stored: whichever came first, the send's answer or this.
    onMessage: (msg) => {
      if (isOwnMessage(msg)) outbox?.acknowledge(msg.clientMessageId);
    },
    onConversationChanged: (payload) => {
      if (conversationId && payload?.conversationId === conversationId) {
        loadConversation(conversationId);
      }
      loadConversations?.();
    },
  });

  // Load the thread on mount and when the open conversation changes. A thread
  // created here was already loaded by createConversation.
  useEffect(() => {
    if (currentUserId && loadedIdRef.current !== conversationId) {
      loadConversation(conversationId);
    }
  }, [currentUserId, conversationId]);

  // A message the server stored, from the outbox: into this thread when it is this one's.
  useEffect(() => {
    if (!storedListeners) return undefined;
    const listener = (storedConversationId, message) => {
      if (storedConversationId && storedConversationId === loadedIdRef.current) {
        dispatch({type: "NEW_MESSAGE", payload: {...message, conversationId: storedConversationId}});
      }
    };
    storedListeners.add(listener);
    return () => {
      storedListeners.delete(listener);
    };
  }, [storedListeners]);

  // This conversation's messages still on their way (or its draft's, before the first send creates it).
  const outboxEntries = useSyncExternalStore(outbox ? outbox.subscribe : noSubscribe, outbox ? outbox.getSnapshot : noEntries);
  const draftKey = draftKeyOf(activeConversation);
  const pendingHere = useMemo(
    () => entriesOf(outboxEntries, conversationId || draftKey),
    [outboxEntries, conversationId, draftKey]
  );

  // Auto-scroll to bottom when new messages arrive, the patient's own on their way included
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedChat, pendingHere.length]);

  // Load the thread from the patient route. The server page already applies
  // the patient's history visibility. A draft has no thread yet.
  const loadConversation = async (targetId) => {
    loadedIdRef.current = targetId;
    if (!targetId) {
      dispatch({type: "SET_MESSAGES", payload: []});
      return;
    }

    dispatch({type: "SET_LOADING", payload: true});

    try {
      const page = await patientMessagingApi.listMessages(auth, targetId);
      const sortedMessages = [...page.messages].sort((a, b) => {
        return new Date(a.timestamp) - new Date(b.timestamp);
      });
      dispatch({type: "SET_MESSAGES", payload: sortedMessages});
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast({
        title: "Error loading messages",
        status: "error",
        duration: 3000,
      });
    } finally {
      dispatch({type: "SET_LOADING", payload: false});
    }
  };

  // A draft becomes a server conversation on the first send. Returns the server
  // id, or null when the clinic does not let the patient start a conversation.
  const createConversation = async () => {
    try {
      const result = await ensureServerConversation({
        conversation: activeConversation,
        auth,
        api: patientMessagingApi,
      });
      if (result.blocked) {
        setInitiateBlocked(true);
        return null;
      }

      const serverId = result.conversation.conversationId;
      setActiveConversation(result.conversation);
      // An existing pair has history: show it before this send is echoed.
      if (result.created) {
        loadedIdRef.current = serverId;
      } else {
        await loadConversation(serverId);
      }

      // Join the conversation room for real-time messages
      if (socket.current && socket.current.connected) {
        socket.current.emit("join-room", serverId);
      }

      onConversationCreated?.(result.conversation);
      return serverId;
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast({
        title: "Error creating conversation",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
      throw error; // Re-throw to prevent message sending
    }
  };

  // The conversation's id for a send: this one's, or — a draft — the one its
  // first send creates, once (later sends of the same draft reuse it).
  const createdIdRef = useRef(null);
  const conversationIdForSend = async () => {
    if (conversationId) return conversationId;
    if (createdIdRef.current) return createdIdRef.current;
    const id = await createConversation();
    if (!id) throw initiateBlockedError();
    createdIdRef.current = id;
    return id;
  };

  // One file, as its message needs it before it is sent: uploaded into the
  // conversation's folder. Its failure is the send's, shown with Retry.
  const uploadFor = (file) => async () => {
    const targetId = await conversationIdForSend();
    const uploadResponse = await crudStorageUpload(schState, file, "brightlighthealth", `messages/${targetId}/`);
    const serverFileName = uploadResponse?.data?.fileName;
    if (typeof serverFileName !== "string" || serverFileName.length === 0) {
      throw new Error(`${file.name} could not be uploaded.`);
    }
    return {
      conversationId: targetId,
      message: file.name,
      attachment: {path: `messages/${targetId}/`, fileName: serverFileName, originalName: file.name, type: file.type, size: file.size},
    };
  };

  // Send, in the patient's order: each file, then the text. Each goes into the
  // outbox at once (outbox.js; ADR 0018 §5), which shows it "Sending…" here and
  // keeps it until the server has stored it: nothing typed or picked is lost,
  // and Send is never blocked by the one before.
  const handleSendMessage = (messageContent) => {
    if (!canSendMessage) {
      toast({
        title: "Messaging unavailable",
        description: "You can only respond to conversations sent to you.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    if (!outbox) return;

    let content = messageContent;
    if (typeof messageContent !== "string") {
      // Serialize Slate content to string
      content = messageContent?.map((n) => Node.string(n)).join("\n") || "";
    }
    const files = selectedFiles;
    if (files.length === 0 && !content?.trim()) return;

    const key = conversationId || draftKey;
    const draftPrepare = conversationId ? null : conversationIdForSend;
    for (const file of files) {
      outbox.enqueue({conversationId: key, clientMessageId: nanoid(), messageType: "file", message: file.name, prepare: uploadFor(file)});
    }
    if (files.length > 0) {
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    if (content?.trim()) {
      outbox.enqueue({
        ...buildPatientSend({conversationId: key, text: content.trim(), newId: nanoid}),
        prepare: draftPrepare,
      });
    }
  };

  // Format message time
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);

    if (isToday(date)) {
      return format(date, "h:mm a");
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, "h:mm a")}`;
    } else {
      return format(date, "MMM d 'at' h:mm a");
    }
  };

  // Format date divider
  const formatDateDivider = (date) => {
    if (isToday(date)) {
      return "Today";
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "EEEE, MMMM d");
    }
  };

  // Check if user can delete a message (within 30 minutes of sending)
  const canDeleteMessage = (message) => {
    if (!message || !message.messageId) return false;

    // Only allow deleting own messages
    const isOwner = isOwnMessage(message);

    // Add time limit - can only delete within 30 minutes
    const timeDiff = Date.now() - new Date(message.timestamp).getTime();
    return isOwner && timeDiff <= 30 * 60 * 1000; // 30 minutes
  };

  // Handle delete message
  const handleDeleteMessage = async (messageId) => {
    const result = await deletePatientMessage({
      auth,
      api: patientMessagingApi,
      conversationId,
      messageId,
    });
    if (result.ok) {
      dispatch({type: "MESSAGE_DELETED", payload: {messageId, conversationId}});
      toast({
        title: "Message deleted",
        status: "success",
        duration: 2000,
      });
    } else {
      toast({
        title: "Failed to delete message",
        description: result.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    if (!fileType) return FiFile;
    if (fileType.startsWith("image/")) return FiImage;
    return FiFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = new Date(message.timestamp);
      if (!currentDate || !isSameDay(currentDate, messageDate)) {
        currentDate = messageDate;
        groups.push({
          date: messageDate,
          messages: [message],
        });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });

    return groups;
  };

  // Handle file select
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  // Handle files dropped via drag and drop
  const handleFilesDropped = (fileList) => {
    const files = Array.from(fileList);
    processFiles(files);
  };

  // Process files (shared logic for both file input and drag/drop)
  const processFiles = (files) => {
    // Check if we're adding too many files (limit to 10 total)
    if (selectedFiles.length + files.length > 10) {
      toast({
        title: "Too many files",
        description: "You can only send up to 10 files at once",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of files) {
      // Check file size (10MB limit per file)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: `File too large: ${file.name}`,
          description: "Each file must be smaller than 10MB",
          status: "error",
          duration: 3000,
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const inGroup = activeConversation?.kind === "group";
  const members = activeConversation?.members || [];
  const isMe = (kind, id) => kind === "patient" && id === currentUserId;
  const myMember = members.find((member) => isMe(member.kind, member.id));

  // The other person of a direct conversation.
  let clinicianData = useMemo(() => {
    if (inGroup) return null;
    return members.find((member) => !isMe(member.kind, member.id)) || null;
  }, [activeConversation, currentUserId])

  // The member who sent a message. A message without senderKind matches by id.
  const senderOf = (message) =>
    members.find(
      (member) =>
        member.id === message.senderId &&
        (!message.senderKind || member.kind === message.senderKind)
    ) || null;

  // Own message: sent by this patient (a null senderKind is a legacy row).
  const isOwnMessage = (message) =>
    message.senderId === currentUserId &&
    (message.senderKind === "patient" || message.senderKind == null);

  return (
    <Flex h="100%" w="100%" direction="column" bg="white" position="relative">
      {/* Header */}
      <HStack
        px={5}
        py={2.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="white"
        spacing={3}
        h="52px"
        flexShrink={0}
      >
        <IconButton
          icon={<IoArrowBack />}
          size="sm"
          variant="ghost"
          color="gray.500"
          display={{base: "flex", md: "none"}}
          onClick={onBack}
          aria-label="Back"
        />

        {/* Channel/User Info */}
        <HStack flex={1} spacing={2.5}>
          {inGroup ? (
            <>
              <Flex
                w="32px"
                h="32px"
                bg="#f0f0ff"
                rounded="lg"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={FiHash} color="#5754FF" fontSize="15px" />
              </Flex>
              <Box>
                <Text fontSize="15px" fontWeight="600" color="#1a1f36" lineHeight="1.2">
                  {activeConversation?.title}
                </Text>
                <Text fontSize="11px" color="gray.400" lineHeight="1.2">
                  {members.length} members
                </Text>
              </Box>
            </>
          ) : (
            <>
              <CSSAvatar user={clinicianData} size="xs" />
              <Box>
                <Text fontSize="15px" fontWeight="600" color="#1a1f36" lineHeight="1.2">
                  {clinicianData?.name}
                </Text>
                <Text fontSize="11px" color="gray.400" lineHeight="1.2">
                  Direct message
                </Text>
              </Box>
            </>
          )}
        </HStack>

        {/* Header Actions */}
        {inGroup && openGroupManagement && (
          <Tooltip
            label={
              myMember?.isAdmin
                ? "Manage group"
                : "Group info"
            }
          >
            <IconButton
              icon={<IoSettingsOutline />}
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{color: "#5754FF", bg: "#f0f0ff"}}
              onClick={() => openGroupManagement(activeConversation)}
              aria-label="Group info"
            />
          </Tooltip>
        )}
      </HStack>

      {/* Messages Area */}
      <Box
        ref={scrollRef}
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        px={{base: 4, md: 6}}
        py={5}
        bg="white"
        minH={0}
        className="sbar3"
      >
        {loading ? (
          <Flex justify="center" align="center" h="full">
            <Spinner size="md" color="#5754FF" thickness="2px" />
          </Flex>
        ) : (!messages || messages.length === 0) && pendingHere.length === 0 ? (
          <Flex justify="center" align="center" h="full">
            <VStack spacing={2}>
              <Flex
                w="48px"
                h="48px"
                bg="#f0f0ff"
                rounded="full"
                align="center"
                justify="center"
                mb={1}
              >
                <Icon as={FiHash} color="#5754FF" fontSize="20px" />
              </Flex>
              <Text color="#1a1f36" fontSize="15px" fontWeight="600">
                Start a conversation
              </Text>
              <Text color="gray.400" fontSize="13px" textAlign="center">
                Send a message to {clinicianData?.name || activeConversation?.title}
              </Text>
            </VStack>
          </Flex>
        ) : (
          <VStack spacing={0} align="stretch">
            {groupMessagesByDate(messages).map((group, groupIndex) => (
              <Box key={groupIndex}>
                {/* Date Divider */}
                <Flex align="center" my={5} gap={3}>
                  <Divider borderColor="gray.200" />
                  <Text
                    fontSize="11px"
                    color="gray.400"
                    px={3}
                    py={1}
                    bg="white"
                    rounded="full"
                    flexShrink={0}
                    fontWeight="500"
                    borderWidth="1px"
                    borderColor="gray.100"
                    letterSpacing="0.02em"
                  >
                    {formatDateDivider(group.date)}
                  </Text>
                  <Divider borderColor="gray.200" />
                </Flex>

                {/* Messages for this date */}
                {group.messages.map((message, index) => {
                  const isOwn = isOwnMessage(message);
                  const showAvatar =
                    index === 0 ||
                    group.messages[index - 1]?.senderId !== message.senderId;

                  // Check if this is a new message from same user after time gap (5 minutes)
                  const isNewAfterGap =
                    index > 0 &&
                    !showAvatar &&
                    group.messages[index - 1]?.senderId === message.senderId &&
                    new Date(message.timestamp) -
                      new Date(group.messages[index - 1]?.timestamp) >
                      300000;

                  // Check if next message is from different user or if this is the last message
                  const isLastInGroup =
                    index === group.messages.length - 1 ||
                    group.messages[index + 1]?.senderId !== message.senderId;

                  // Bubble border radius — round all corners except the corner closest to the sender
                  const ownRadius = showAvatar
                    ? "18px 18px 4px 18px"
                    : isLastInGroup
                    ? "18px 4px 18px 18px"
                    : "18px 4px 4px 18px";
                  const otherRadius = showAvatar
                    ? "18px 18px 18px 4px"
                    : isLastInGroup
                    ? "4px 18px 18px 18px"
                    : "4px 18px 18px 4px";

                  return (
                    <Flex
                      key={message.messageId || index}
                      justify={isOwn ? "flex-end" : "flex-start"}
                      mb={isLastInGroup ? 4 : 1}
                      mt={isNewAfterGap ? 4 : 0}
                      className="message-fade-in"
                    >
                      <Flex
                        maxW={{base: "85%", md: "65%"}}
                        direction={isOwn ? "row-reverse" : "row"}
                        align="flex-end"
                        gap={2}
                      >
                        {/* Avatar — only for other users */}
                        {!isOwn && (
                          <Box w="30px" flexShrink={0} mb="2px">
                            {showAvatar || isNewAfterGap ? (
                              <CSSAvatar
                                user={
                                  inGroup && senderOf(message)
                                    ? senderOf(message)
                                    : clinicianData
                                }
                                size="sm"
                              />
                            ) : (
                              <Box w="30px" />
                            )}
                          </Box>
                        )}

                        {/* Message Content */}
                        <VStack align={isOwn ? "flex-end" : "flex-start"} spacing={0.5} role="group">
                          {/* Sender name for group chats or first in sequence */}
                          {!isOwn && (showAvatar || isNewAfterGap) && (
                            <Text
                              fontSize="12px"
                              fontWeight="600"
                              color="gray.500"
                              px={1}
                              mb={0.5}
                            >
                              {senderOf(message)?.name || clinicianData?.name || activeConversation?.title}
                            </Text>
                          )}

                          {/* File message */}
                          {message.messageType === "file" ||
                          message.messageType === "files" ? (
                            message.isDeleted ? (
                              <Box
                                px={4}
                                py={2.5}
                                bg={isOwn ? "#eeeeff" : "white"}
                                borderRadius={isOwn ? ownRadius : otherRadius}
                                borderWidth={isOwn ? 0 : "1px"}
                                borderColor="gray.100"
                              >
                                <Text
                                  fontStyle="italic"
                                  color="gray.400"
                                  fontSize="13px"
                                >
                                  File deleted
                                </Text>
                              </Box>
                            ) : (
                              <Flex align="center" gap={1}>
                                {/* Delete menu — before bubble for own messages */}
                                {isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                  <Menu placement="bottom-end">
                                    <MenuButton
                                      as={IconButton}
                                      icon={<BsThreeDots />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.300"
                                      opacity={0}
                                      _groupHover={{opacity: 1}}
                                      transition="opacity 0.15s"
                                      _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                      aria-label="Message options"
                                      h="20px"
                                      w="20px"
                                      minW="20px"
                                    />
                                    <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                      <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                        Delete
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                )}
                                <Box minW="220px" maxW="320px">
                                  <Tooltip label="Click to preview" fontSize="xs" placement="top">
                                    <HStack
                                      px={4}
                                      py={3}
                                      bg={isOwn ? "#5754FF" : "white"}
                                      borderRadius={isOwn ? ownRadius : otherRadius}
                                      borderWidth={isOwn ? 0 : "1px"}
                                      borderColor="gray.100"
                                      spacing={3}
                                      cursor="pointer"
                                      _hover={{
                                        bg: isOwn ? "#4644e0" : "gray.50",
                                      }}
                                      transition="all 0.15s"
                                      onClick={async () => {
                                        if (
                                          message.attachment?.path &&
                                          message.attachment?.fileName
                                        ) {
                                          try {
                                            const response = await fileProxy(
                                              schState,
                                              "brightlighthealth",
                                              message.attachment.path,
                                              message.attachment.fileName
                                            );

                                            if (response && response.data) {
                                              const blob = new Blob(
                                                [response.data],
                                                {
                                                  type:
                                                    response.headers?.[
                                                      "content-type"
                                                    ] ||
                                                    "application/octet-stream",
                                                }
                                              );
                                              const blobUrl =
                                                URL.createObjectURL(blob);

                                              setFilePreview({
                                                fileName:
                                                  message.attachment.originalName ||
                                                  message.attachment.fileName,
                                                fileUrl: blobUrl,
                                                conversationId,
                                                isBlob: true,
                                              });
                                            } else {
                                              if (message.attachment?.url) {
                                                setFilePreview({
                                                  fileName:
                                                    message.attachment.originalName ||
                                                    message.attachment.fileName,
                                                  fileUrl: message.attachment.url,
                                                  conversationId,
                                                });
                                              } else {
                                                toast({
                                                  title: "Unable to preview file",
                                                  description: "The file may not be available.",
                                                  status: "error",
                                                  duration: 5000,
                                                });
                                              }
                                            }
                                          } catch (error) {
                                            console.error("Error getting file URL:", error);
                                            toast({
                                              title: "Failed to open file",
                                              status: "error",
                                              duration: 3000,
                                            });
                                          }
                                        }
                                      }}
                                    >
                                      <Icon
                                        as={getFileIcon(message.attachment?.type)}
                                        color={isOwn ? "whiteAlpha.800" : "gray.400"}
                                        fontSize="20px"
                                        flexShrink={0}
                                      />
                                      <VStack align="start" spacing={0} flex={1}>
                                        <Text
                                          fontSize="13px"
                                          fontWeight="500"
                                          color={isOwn ? "white" : "#1a1f36"}
                                          noOfLines={1}
                                        >
                                          {message.attachment?.originalName ||
                                            message.attachment?.name ||
                                            message.message}
                                        </Text>
                                        <Text fontSize="11px" color={isOwn ? "whiteAlpha.700" : "gray.400"}>
                                          {message.attachment?.size
                                            ? formatFileSize(message.attachment.size)
                                            : "File"}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </Tooltip>
                                </Box>
                                {/* Delete menu — after bubble for other messages */}
                                {!isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                  <Menu placement="bottom-end">
                                    <MenuButton
                                      as={IconButton}
                                      icon={<BsThreeDots />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.300"
                                      opacity={0}
                                      _groupHover={{opacity: 1}}
                                      transition="opacity 0.15s"
                                      _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                      aria-label="Message options"
                                      h="20px"
                                      w="20px"
                                      minW="20px"
                                    />
                                    <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                      <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                        Delete
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                )}
                              </Flex>
                            )
                          ) : (
                            /* Regular text message */
                            <Flex align="center" gap={1}>
                              {/* Delete menu — before bubble for own messages */}
                              {isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                <Menu placement="bottom-end">
                                  <MenuButton
                                    as={IconButton}
                                    icon={<BsThreeDots />}
                                    size="xs"
                                    variant="ghost"
                                    color="gray.300"
                                    opacity={0}
                                    _groupHover={{opacity: 1}}
                                    transition="opacity 0.15s"
                                    _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                    aria-label="Message options"
                                    h="20px"
                                    w="20px"
                                    minW="20px"
                                  />
                                  <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                    <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              )}
                              {message.isDeleted ? (
                                <Box
                                  px={4}
                                  py={2.5}
                                  bg={isOwn ? "#eeeeff" : "white"}
                                  borderRadius={isOwn ? ownRadius : otherRadius}
                                  borderWidth={isOwn ? 0 : "1px"}
                                  borderColor="gray.100"
                                >
                                  <Text
                                    fontStyle="italic"
                                    color="gray.400"
                                    fontSize="13px"
                                  >
                                    Message deleted
                                  </Text>
                                </Box>
                              ) : (
                                <Box
                                  px={4}
                                  py={2.5}
                                  bg={isOwn ? "#5754FF" : "white"}
                                  borderRadius={isOwn ? ownRadius : otherRadius}
                                  borderWidth={isOwn ? 0 : "1px"}
                                  borderColor="gray.100"
                                  fontSize="14px"
                                  lineHeight="1.5"
                                  color={isOwn ? "white" : "#1a1f36"}
                                  boxShadow={isOwn ? "none" : "0 1px 2px rgba(0,0,0,0.04)"}
                                >
                                  <FormattedText
                                    text={message.message}
                                    color={isOwn ? "white" : "#1a1f36"}
                                  />
                                </Box>
                              )}
                              {/* Delete menu — after bubble for other messages */}
                              {!isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                <Menu placement="bottom-end">
                                  <MenuButton
                                    as={IconButton}
                                    icon={<BsThreeDots />}
                                    size="xs"
                                    variant="ghost"
                                    color="gray.300"
                                    opacity={0}
                                    _groupHover={{opacity: 1}}
                                    transition="opacity 0.15s"
                                    _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                    aria-label="Message options"
                                    h="20px"
                                    w="20px"
                                    minW="20px"
                                  />
                                  <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                    <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              )}
                            </Flex>
                          )}

                          {/* Timestamp — shown on last message in a group */}
                          {isLastInGroup && (
                            <Text
                              fontSize="10px"
                              color="gray.400"
                              px={1}
                              mt={0.5}
                            >
                              {formatMessageTime(message.timestamp)}
                            </Text>
                          )}
                        </VStack>
                      </Flex>
                    </Flex>
                  );
                })}
              </Box>
            ))}

            {otherUserTyping && (
              <Flex justify="flex-start" mt={2}>
                <Flex align="flex-end" gap={2}>
                  <Box w="30px" flexShrink={0}>
                    <CSSAvatar user={clinicianData || activeConversation} size="sm" />
                  </Box>
                  <Box
                    px={4}
                    py={2.5}
                    bg="white"
                    borderRadius="18px 18px 18px 4px"
                    borderWidth="1px"
                    borderColor="gray.100"
                  >
                    <HStack spacing={1}>
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" />
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" style={{animationDelay: "0.2s"}} />
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" style={{animationDelay: "0.4s"}} />
                    </HStack>
                  </Box>
                </Flex>
              </Flex>
            )}
          </VStack>
        )}
        <PendingMessages entries={pendingHere} onRetry={outbox?.retry} onRemove={outbox?.discard} />
      </Box>

      {/* Message Input */}
      <Input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.txt"
      />

      <Box borderTop="1px solid" borderColor="gray.100" flexShrink={0} bg="white">
        {canSendMessage && !initiateBlocked ? (
          <SimpleRichTextEditor
            onSendMessage={handleSendMessage}
            onFileAttach={() => fileInputRef.current?.click()}
            onFilesDropped={handleFilesDropped}
            onPatientFilesClick={() => {}}
            showPatientFiles={false}
            showAttachmentButton={true}
            onTyping={() => {}}
            placeholder={
              inGroup
                ? `Message #${activeConversation?.title}`
                : `Message ${clinicianData?.name}`
            }
            selectedFiles={selectedFiles}
            onRemoveFile={(index) => {
              if (index !== undefined) {
                setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
              } else {
                setSelectedFiles([]);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }
            }}
            getFileIcon={getFileIcon}
            formatFileSize={formatFileSize}
          />
        ) : (
          <Flex p={3} bg="gray.50" align="center" justify="center">
            <Text fontSize="13px" color="gray.500">
              {initiateBlocked
                ? "Your clinic does not let patients start new conversations. You can reply when a staff member messages you."
                : "You can only respond to conversations sent to you."}
            </Text>
          </Flex>
        )}
      </Box>

      <style>{`
        @keyframes typingBubble {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.85); }
          30% { opacity: 1; transform: scale(1); }
        }
        .typing-dot-bubble {
          animation: typingBubble 1.4s infinite;
        }
        @keyframes messageFadeIn {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .message-fade-in {
          animation: messageFadeIn 0.2s ease-out;
        }
      `}</style>

      {/* File Preview Modal */}
      {filePreview && (
        <MessagingFilePreview
          preview={filePreview}
          setPreview={setFilePreview}
          state={schState}
        />
      )}
    </Flex>
  );
}
